import { NavLink, Outlet } from "react-router-dom";
import { useTheme } from "../theme/ThemeProvider.js";
import { useLogout } from "../api/queries.js";

/* ── SVG Icons ──────────────────────────────────────── */
const IconDashboard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="2"/>
    <rect x="13" y="3" width="8" height="5" rx="2"/>
    <rect x="13" y="10" width="8" height="11" rx="2"/>
    <rect x="3" y="13" width="8" height="8" rx="2"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4.5" width="18" height="16" rx="3"/>
    <path d="M3 9h18M8 2.5v4M16 2.5v4"/>
  </svg>
);
const IconPayments = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M15.5 9.2a4 4 0 1 0 0 5.6M8.5 11h5.5M8.5 13.2h5.5"/>
  </svg>
);
const IconReports = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M6 21V11M12 21V5M18 21v-7"/>
  </svg>
);
const IconSettings = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h6M14 18h6"/>
    <circle cx="15" cy="6" r="2.1"/>
    <circle cx="9" cy="12" r="2.1"/>
    <circle cx="12" cy="18" r="2.1"/>
  </svg>
);
const IconSun = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 3v1.5M12 19.5V21M5 5l1 1M18 18l1 1M3 12h1.5M19.5 12H21M5 19l1-1M18 6l1-1"/>
  </svg>
);
const IconMoon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.6 6.6 0 0 0 9.8 9.8Z"/>
  </svg>
);

/* ── Nav items ──────────────────────────────────────── */
const NAV_ITEMS = [
  { to: "/",          label: "Home",     Icon: IconDashboard, end: true  },
  { to: "/calendar",  label: "Calendar", Icon: IconCalendar,  end: false },
  { to: "/payments",  label: "Payments", Icon: IconPayments,  end: false },
  { to: "/reports",   label: "Reports",  Icon: IconReports,   end: false },
  { to: "/settings",  label: "Settings", Icon: IconSettings,  end: false },
] as const;

/* ── Layout ─────────────────────────────────────────── */
export function Layout() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const logout = useLogout();

  return (
    <div className="app-shell">
      {/* ── Desktop sidebar ───────────────────────────── */}
      <aside className="sidebar" aria-label="Main navigation">
        {/* Brand */}
        <div className="sidebar__brand">
          <div className="sun-badge sm">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 3v1.5M12 19.5V21M5 5l1 1M18 18l1 1M3 12h1.5M19.5 12H21M5 19l1-1M18 6l1-1"/>
            </svg>
          </div>
          <span className="sidebar__brand-name">Sundial</span>
        </div>

        {/* Nav links */}
        <nav className="sidebar__nav">
          {NAV_ITEMS.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `sidebar__item${isActive ? " active" : ""}`}
            >
              <Icon />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer: theme toggle + logout */}
        <div className="sidebar__footer">
          <button className="sidebar__theme-btn" onClick={toggle} aria-label="Toggle theme">
            {isDark ? <IconSun /> : <IconMoon />}
            {isDark ? "Switch to daylight" : "Switch to night"}
          </button>
          <button
            className="sidebar__theme-btn"
            onClick={() => logout.mutate()}
            aria-label="Log out"
            style={{ color: "var(--muted)", marginTop: 4 }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
            Log out
          </button>
        </div>
      </aside>

      {/* ── Main content area ─────────────────────────── */}
      <div className="page-scroll">
        <Outlet />
      </div>

      {/* ── Mobile bottom tab bar ─────────────────────── */}
      <nav className="tabbar" aria-label="Main navigation">
        {NAV_ITEMS.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `tabbar__item${isActive ? " active" : ""}`}
            aria-label={label}
          >
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
